import { Preferences } from "@capacitor/preferences";
import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import {
  type KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Link,
  LoaderFunction,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { get } from "api";
import type { Models } from "api/types";
import ChevronDown from "atoms/icon/icons/chevron_down_small.svg";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { SearchField } from "atoms/search-field/SearchField";
import { Spinner } from "atoms/Spinner";
import { TextButton } from "atoms/text-button/TextButton";
import { useCreateMultiPlatformUser } from "hooks/useCreateMultiPlatformUser";
import useNavigateToPlatformCompleteProfile from "hooks/useNavigateToPlatformCompleteProfile";
import { PlatformWithLocationsList } from "organisms/PlatformWithLocationsList/PlatformWithLocationsList";
import { queryClient, ThemeContext } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async () => {
  const user = await queryClient
    .fetchQuery({
      queryKey: ["/user"],
      queryFn: () => get("/user"),
      staleTime: 1000 * 60 * 2,
    })
    .catch(() => null);

  const multiPlatformVerifyToken = Preferences.get({
    key: "multiPlatformVerifyToken",
  });

  return waitObj({ user, multiPlatformVerifyToken });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const searchLocationLoader: LoaderFunction = () => {
  return load();
};

const getSearchResults = (
  pages: InfiniteData<
    Models<"GetMultiPlatformLocationsResultPagedResults">
  >["pages"] = []
) =>
  pages.reduce(
    (accumulator, page) => accumulator.concat(page.items || []),
    [] as Models<"GetMultiPlatformLocationsResult">[]
  ) || [];

export const SearchLocationPage = () => {
  const { user, multiPlatformVerifyToken } = useLoaderData() as LoaderData;
  const navigateToPlatformCompleteProfile =
    useNavigateToPlatformCompleteProfile();
  const createMultiPlatformUserMutation = useCreateMultiPlatformUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const prevState = useLocation();
  const [searchParams] = useSearchParams();
  const { resetTheme } = useContext(ThemeContext);

  const {
    data: searchResults,
    /**
     * We use `isFetching` here instead of `isLoading` because
     * of a known quirky behavior of `isLoading` property when
     * `enabled` property is used.
     * @see {@link https://github.com/TanStack/query/issues/3584}
     */
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    retry: false,
    enabled: !!debouncedSearchQuery,
    staleTime: Infinity,
    queryKey: ["/multi-platform/locations", debouncedSearchQuery],
    queryFn: ({ pageParam = 1, queryKey }) => {
      const query = queryKey[queryKey.length - 1];
      return get("/multi-platform/locations", {
        page: pageParam,
        searchQuery: query, // Using unified search parameter
        mileRadius: 50,
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.totalPages > lastPage.page) {
        return lastPage.page + 1;
      }
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page !== 1) {
        return firstPage.page - 1;
      }
    },
  });

  useEffect(() => {
    setSearchQuery(prevState?.state?.zip || "");
  }, [prevState]);

  const updateDebouncedSearch = useRef(
    debounce((query) => setDebouncedSearchQuery(query), 700)
  ).current;

  useEffect(() => {
    updateDebouncedSearch(searchQuery);
  }, [searchQuery, updateDebouncedSearch]);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  const referralCode = searchParams.get("referralCode");

  const allSearchResults = getSearchResults(searchResults?.pages);
  const isUserBeingCreated =
    createMultiPlatformUserMutation.isLoading ||
    createMultiPlatformUserMutation.isSuccess;

  return (
    <div>
      <div className="sticky top-0 w-full bg-white pl-3 pt-[env(safe-area-inset-top)]">
        <span className="flex items-center justify-between">
          <Link to="..">
            <IconButton size="small" style="iconOnly" svg={ChevronLeft} />
          </Link>

          <div className="text-sub2 uppercase">Search by location</div>

          <div className="invisible">
            <IconButton size="small" style="iconOnly" svg={ChevronLeft} />
          </div>
        </span>
      </div>

      <div className="flex flex-col gap-4 p-4">
        <SearchField
          placeholder="City, zip code, or practice name"
          autoFocus
          label="Search"
          name="search_query"
          value={searchQuery}
          onChange={setSearchQuery}
          onKeyDown={handleInputKeyDown}
          onIconClick={(side) => side == "right" && setSearchQuery("")}
        />

        {isUserBeingCreated || (isFetching && !allSearchResults.length) ? (
          <div className="mt-10 flex flex-col items-center justify-center gap-6">
            <Spinner />
            {!isUserBeingCreated && (
              <div className="text-body2 text-secondary">Searching medspas</div>
            )}
          </div>
        ) : (
          <div>
            {!!searchQuery && (
              <div className="mb-2 text-body2 text-secondary">
                {searchResults?.pages?.[0]?.count ?? 0} results found
              </div>
            )}

            {!!searchQuery && !allSearchResults.length && (
              <div className="mt-10 flex flex-col items-center justify-center text-center">
                <div className="mb-2 text-body1">No results found</div>
                <div className="mb-2 text-body2 text-secondary">
                  Not the result you expected?
                </div>
                <a
                  href="mailto:support@repeatmd.com"
                  className="text-brand-color"
                >
                  App support
                </a>
              </div>
            )}

            <PlatformWithLocationsList
              platformLocations={allSearchResults}
              onItemClick={async (platformId: string, locationId: string) => {
                if (
                  !!multiPlatformVerifyToken?.value ||
                  user?.isGuest === false
                ) {
                  navigateToPlatformCompleteProfile({
                    platformId,
                    locationId,
                    referralCode: referralCode || "",
                    searchQuery: searchQuery,
                  });
                  resetTheme();
                } else {
                  createMultiPlatformUserMutation.mutate({
                    platformLocationId: locationId,
                    platformId,
                    userId: user?.id,
                  });
                }
              }}
            />
            {hasNextPage && (
              <div className="flex items-center justify-center py-4">
                <TextButton
                  disabled={isFetching}
                  onClick={() => fetchNextPage()}
                  endIconSVG={ChevronDown}
                >
                  Load more results
                </TextButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
